import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import type { SlackIntegration } from "../../../../api/client.ts";
import ConfirmDialog from "../../../../components/ConfirmDialog.tsx";
import SlackLogo from "../../../../components/SlackLogo.tsx";
import AccordionCard from "../../../../components/AccordionCard.tsx";
import type { Status } from "../../../../components/StatusIcon.tsx";

interface SlackIntegrationSectionProps {
  integration: SlackIntegration | null | undefined;
  isLoading: boolean;
  deleting: boolean;
  defaultExpanded?: boolean;
  onConnect: () => void;
  /** Resolves once disconnected; the confirmation stays open (spinning) until then. */
  onDisconnect: () => Promise<unknown>;
}

export default function SlackIntegrationSection({
  integration,
  isLoading,
  deleting,
  defaultExpanded,
  onConnect,
  onDisconnect,
}: SlackIntegrationSectionProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  let status: Status;
  if (isLoading) status = { kind: "loading" };
  else if (integration) status = { kind: "ok", label: "Connected" };
  else status = { kind: "off", label: "Not connected" };

  return (
    <AccordionCard
      logo={<SlackLogo />}
      title="Slack"
      summary={
        integration ? (
          <>
            Connected to <strong>{integration.team_name}</strong>
          </>
        ) : (
          "Connect your organization's Slack workspace."
        )
      }
      status={status}
      defaultExpanded={defaultExpanded}
    >
      {integration ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1">
              Connected to <strong>{integration.team_name}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Scopes: {integration.scopes.join(", ") || "—"}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmOpen(true)}
          >
            Disconnect
          </Button>
        </Stack>
      ) : (
        !isLoading && (
          <Button variant="contained" onClick={onConnect}>
            Add to Slack
          </Button>
        )
      )}
      <ConfirmDialog
        open={confirmOpen}
        title="Disconnect Slack"
        confirmLabel="Disconnect"
        pending={deleting}
        onConfirm={() =>
          void onDisconnect()
            .then(() => setConfirmOpen(false))
            .catch(() => {})
        }
        onClose={() => setConfirmOpen(false)}
      >
        Disconnect <strong>{integration?.team_name}</strong>? Workflows stop posting to its channels
        until you connect Slack again.
      </ConfirmDialog>
    </AccordionCard>
  );
}
